import type { FormFieldRef } from "../../components/forms/types";
import type { SectionComponentRefs } from "../../pages/form-page/_components/section";
import type { FieldDto } from "../../types/field";
import type { FormFieldAnswer } from "../../types/submit";
import type { ValuesByName } from "../../types/visibility";
import { resolveFieldType } from "./normalizeFieldType";
import {
  dateRangeValidationMessage,
  parseDateRangeValue,
} from "./dateRangeField";
import { isDateWithTime } from "./fieldOptions";
import { isDisplayFieldType } from "../formBuilder/apiFieldTypes";
import { isFieldVisible, normalizeValue } from "./visibility";

function parseCityId(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return Math.trunc(parsed);
}

/** Значение для POST /submit: city — число, date-range — объект. */
export function formatSubmitFieldValue(
  field: FieldDto,
  raw: string,
): FormFieldAnswer["value"] | null {
  const normalized = normalizeValue(raw);
  if (normalized === "") return null;

  if (isDisplayFieldType(field.type)) return null;

  if (resolveFieldType(field.type) === "date-range") {
    const range = parseDateRangeValue(normalized);
    return range &&
      dateRangeValidationMessage(range, isDateWithTime(field), true) == null
      ? range
      : null;
  }

  if (resolveFieldType(field.type) === "city") {
    return parseCityId(normalized);
  }

  if (resolveFieldType(field.type) === "privacy") {
    return normalized === "1" ? "1" : null;
  }

  return normalized;
}

function findFieldRef(
  sectionsRefs: Array<SectionComponentRefs | null | undefined>,
  fieldName: string,
): FormFieldRef | undefined {
  for (const section of sectionsRefs) {
    const ref = section?.refs.current.get(fieldName);
    if (ref) return ref;
  }
  return undefined;
}

export function buildSubmitPayload(
  fields: FieldDto[],
  valuesByName: ValuesByName,
): FormFieldAnswer[] {
  const payload: FormFieldAnswer[] = [];

  for (const field of fields) {
    if (field.id == null) continue;
    if (isDisplayFieldType(field.type)) continue;
    if (!isFieldVisible(field, valuesByName)) continue;

    const formatted = formatSubmitFieldValue(
      field,
      valuesByName[field.name] ?? "",
    );
    if (formatted === null) continue;

    payload.push({
      field_id: field.id,
      value: formatted,
    });
  }

  return payload;
}

/** Собирает ответы из ref полей (источник истины при submit). */
export function buildSubmitPayloadFromRefs(
  fields: FieldDto[],
  sectionsRefs: Array<SectionComponentRefs | null | undefined>,
  visibleByName: Record<string, boolean>,
): FormFieldAnswer[] {
  const payload: FormFieldAnswer[] = [];

  for (const field of fields) {
    if (field.id == null) continue;
    if (isDisplayFieldType(field.type)) continue;
    if (visibleByName[field.name] === false) continue;

    const ref = findFieldRef(sectionsRefs, field.name);
    const formatted = formatSubmitFieldValue(field, ref?.value ?? "");
    if (formatted === null) continue;

    const labeled = ref?.labeledChoice;
    const value =
      labeled && String(formatted) === labeled.value ? labeled : formatted;

    payload.push({
      field_id: field.id,
      value,
    });
  }

  return payload;
}
